/**
 * webauthn-errors/index.ts — error resolution functions and browser support check
 *
 * (NIST SP 800-63r4 §6.1 user experience guidance)
 */

export type { WebAuthnUserMessage } from "./types";
import type { WebAuthnUserMessage } from "./types";
import { DOM_EXCEPTION_MESSAGES, SERVER_ERROR_MESSAGES, FALLBACK_MESSAGE } from "./messages";

// ---------------------------------------------------------------------------
// Main resolution function
// ---------------------------------------------------------------------------

/**
 * Resolve a DOMException or server error code to a user-facing message.
 *
 * @param error  A DOMException, Error, or server error code string.
 * @returns      A WebAuthnUserMessage with title, description, and severity.
 */
export function resolveWebAuthnError(error: unknown): WebAuthnUserMessage {
  // Handle DOMException (from navigator.credentials.create/get)
  if (error instanceof DOMException) {
    return DOM_EXCEPTION_MESSAGES[error.name] ?? {
      ...FALLBACK_MESSAGE,
      // Never echo error.name / error.message — raw exception text is not user copy.
      description: "That didn't go through. Try again, or use a different passkey.",
    };
  }

  // Handle server error codes (from API responses)
  if (typeof error === "string") {
    return SERVER_ERROR_MESSAGES[error] ?? FALLBACK_MESSAGE;
  }

  // Handle Error objects with a .code property (from server WebAuthnError)
  if (error instanceof Error) {
    const code = (error as Error & { code?: string }).code;
    // Security-tier-ladder floor refusal (module 04; PR #3020 walk-through
    // find, 2026-09-03 — the add-device dialog showed the generic fallback
    // over an honest refusal). The server sentence already names both rungs
    // through the single display source and appends the row's guidance, so
    // pass it through — a hand-typed "Level N of 4" here would be the second
    // dialect the registry-copy guards exist to forbid. Non-retryable: the
    // identical attempt cannot succeed; the sentence names what changes that.
    if (
      (code === "LADDER_FLOOR_REQUIRED" || code === "LADDER_FLOOR_UNAVAILABLE") &&
      error.message.trim() !== ""
    ) {
      return {
        title: "A stronger session is needed",
        description: error.message,
        retryable: false,
        severity: "amber",
      };
    }
    if (code && SERVER_ERROR_MESSAGES[code]) {
      return SERVER_ERROR_MESSAGES[code];
    }
    // Check for DOMException-like errors wrapped in a plain Error
    for (const [domName, msg] of Object.entries(DOM_EXCEPTION_MESSAGES)) {
      if (error.name === domName || error.message.includes(domName)) {
        return msg;
      }
    }
  }

  return FALLBACK_MESSAGE;
}

/**
 * Check whether the current browser + platform supports WebAuthn with PRF.
 * Returns a user-friendly warning if support is limited.
 */
export function checkWebAuthnSupport(): {
  supported:        boolean;
  prfLikelySupported: boolean;
  warning:          string | null;
} {
  if (!window.PublicKeyCredential) {
    return {
      supported:           false,
      prfLikelySupported:  false,
      warning: "Passkeys are not supported in this browser. Please use Chrome 108+, Safari 16+, or Firefox 119+.",
    };
  }

  // PRF is supported in Chrome 116+, Firefox 119+, Safari 17+
  // We can't detect it directly before attempting, so we check the browser.
  const ua = navigator.userAgent;
  const chromeMatch = ua.match(/Chrome\/(\d+)/);
  const firefoxMatch = ua.match(/Firefox\/(\d+)/);
  const safariMatch = ua.match(/Version\/(\d+).*Safari/);

  const prfLikelySupported = (
    (chromeMatch !== null && parseInt(chromeMatch[1], 10) >= 116) ||
    (firefoxMatch !== null && parseInt(firefoxMatch[1], 10) >= 119) ||
    (safariMatch !== null && parseInt(safariMatch[1], 10) >= 17)
  );

  return {
    supported:          true,
    prfLikelySupported,
    warning: prfLikelySupported
      ? null
      : "Your browser may not support the feature that unlocks your vault. If sign-in fails, use Chrome 116+, Firefox 119+, or Safari 17+.",
  };
}
